<template>
	<div class="is-full-width">
		<div class="is-flex is-flex-direction-row is-flex-wrap-nowrap is-full-height">
			<MonthYearSidebar
				:selected-date="selectedDate"
				:list-months-names="listMonthsNames"
				:all-months-data="allMonthsData"
				:now="now"
				@select-date="selectNewDate($event.year, $event.month)" />
			<div class="is-flex-grow-0 border_left px-6" style="flex-basis: 85%;">
				<UploadFilePanel
					:selected-type-file="selectedTypeFile"
					:selected-date="selectedDate"
					:ledger-notice="ledgerNotice"
					:selected-month-file="selectedMonthFile"
					:previous-month="previousMonth"
					:is-error="isError"
					:is-file-uploaded="isFileUploaded"
					:is-uploading-file="isUploadingFile"
					@download-template="downloadTemplateByTypeFile()"
					@download-file="downloadFile($event.url, $event.name, { year: $event.year, month: $event.month })"
					@request-pick="$refs.fileInput.click()"
					@delete-file="openConfirmRestoreModal()" />
				<div class="is-full-width mt-3" :class="{ 'is-disabled-local': isUploadingFile }">
					<RemunerationsDocsTable v-if="!isError && isFileUploaded"
						:tax_documents="selectedMonthFile.tax_documents" @reload-data="changeYear()" @delete-entry="confirmDeleteEntry" />
					<MsgToUploadFile v-else :isError="isError" :msgError="selectedMonthFile.msg_error" />
				</div>
			</div>
		</div>
		<input ref="fileInput" type="file" accept=".xls, .xlsx" class="is-hidden" @change="handleFile" />
		<input ref="fileInputMassive" type="file" accept=".xls, .xlsx" class="is-hidden"
			@change="(event)=>handleFile(event, true, true)" />
	</div>
</template>
<script>
import { getUrlsDownloadFile, downloadSpreadsheetMonthFile } from "@/api/uploadFiles";
import { downloadBlob } from "@/utils/downloadBlob";
import ConfirmRestore from "@/components/modals/ConfirmRestore";
import Page from "@/components/svg/Page.vue";
import Trash from "@/components/svg/Trash.vue";
import UploadFile from "@/components/svg/UploadFile.vue";
import { notificate } from "@/utils/message";
import {
	ImportRunPollTimeout,
	useImportRunPolling,
} from "@/composables/useImportRunPolling";
import moment from "moment";
import { mapActions, mapState } from "vuex";
import MsgToUploadFile from "../MsgToUploadFile.vue";
import RemunerationsDocsTable from "../RemunerationsDocsTable.vue";
import MonthYearSidebar from "./MonthYearSidebar.vue";
import UploadFilePanel from "./UploadFilePanel.vue";

export default {
	name: "BodyUploadDocs",
	components: {
		RemunerationsDocsTable,
		MsgToUploadFile,
		MonthYearSidebar,
		UploadFilePanel,
	},
	setup() {
		// El pase real de la subida corre en RQ (cola imports): el POST devuelve
		// 202 + run_id y acá se sondea upload_status hasta S/E. El composable se
		// detiene solo al desmontar el modal.
		const { poll: pollImportRun, stop: stopPolling } = useImportRunPolling();
		return { pollImportRun, stopPolling };
	},
	data() {
		return {
			now: null,
			hasCheckedInitialMassive: false,
			massiveFileToUpload: null,
			listMonthsNames: moment.months(),
			allMonthsData: {},
			selectedMonthFile: {},
			previousMonth: {},
			isUploadingFile: false,
			modalDefault: {
				parent: this,
				width: "25%",
				hasModalCard: true,
				canCancel: ["escape", 'button', "outside"],
				customClass: "center-always-modal",
			},
		};
	},
	computed: {
		...mapState("uploadFiles", ["selectedDate", "selectedTypeFile"]),
		...mapState("userAuth", ["user"]),
		// Empresa con mayor subiendo remuneraciones (decisiones §40): el aviso
		// va fijo porque la subida de un mes no pasa por el dry run.
		ledgerNotice() {
			const usesLedger = Boolean(this.user && this.user.has_ledger);
			if (!usesLedger || this.selectedTypeFile.value !== "remuneracion-clf") return "";
			return "La empresa tiene mayor: en los meses que subas dentro de su período, "
				+ "las cuentas de remuneraciones del mayor que marcó el analista pasan a la "
				+ "línea «Ajuste Remuneraciones» y la planilla se muestra en las cuentas RR HH "
				+ "donde la clasifiques. El total de gastos sigue siendo el del mayor.";
		},
		isFileUploaded() {
			return this.selectedMonthFile && this.selectedMonthFile.id
		},
		isError() {
			return this.selectedMonthFile && this.selectedMonthFile.has_error
		}
	},
	mounted() {
		this.now = moment()
		this.setNewSelectedDate({
			year: this.now.year(),
			month: moment.months(this.now.month())
		})

		this.changeYear()
		this.setLastMonth()
	},
	methods: {
		...mapActions("uploadFiles", [
			"setNewSelectedDate",
			"fetchSpreadsheetByMonth",
			"fetchFilesUploadedByYear",
			"uploadSpreadsheetExcel",
			"fetchUploadStatus",
			"deleteSpreadsheetMonth",
			"removeTaxDocument",
		]),
		selectNewDate(newYearSelected, newMonthSelected) {
			if (newYearSelected > this.now.year() + 1) {
				return
			}
			const actualSelected = this.selectedDate.year
			const actualMonthSelected = this.selectedDate.month
			this.setNewSelectedDate({
				year: newYearSelected,
				month: newMonthSelected
			})
			if (newYearSelected != actualSelected) {
				this.changeYear()
			} else if (newMonthSelected != actualMonthSelected) {
				this.changeMonth()
			}
		},
		handleFile(event, isMassive=false, isDryRun=false) {
			this.isUploadingFile = true
			let file = null;
			if (event) {
				file = event.target?.files?.[0];
				if (isMassive && file) {
					this.massiveFileToUpload = file;
				}
			} else if (isMassive && this.massiveFileToUpload) {
				file = this.massiveFileToUpload;
			}

			if (file) {
				const formData = new FormData();
				if (!isMassive && this.selectedMonthFile.id) {
					formData.append('excel_file_id', this.selectedMonthFile.id);
				}
				formData.append('excel_file', file);
				formData.append('is_massive', isMassive);

				if (isMassive && isDryRun) {
					formData.append('dry_run', true);
				}
				formData.append('type', this.selectedTypeFile.value);
				formData.append('year_file', this.selectedDate.year);
				formData.append('month_file', this.selectedDate.month);
				this.uploadSpreadsheetExcel(formData).then((data) => {
					if (data.is_dry_run) {
						this.isUploadingFile = false;
						this.openDryRunModal(data.summary || [], null, data.warnings || []);
						if (this.$refs.fileInputMassive) this.$refs.fileInputMassive.value = '';
						return;
					}
					// 202: la planilla se procesa en RQ. Se sondea la corrida y se
					// sigue con su `result` (la planilla del mes) al terminar.
					return this.pollImportRun(() => this.fetchUploadStatus(data.run_id))
						.then((run) => this.applyUploadRun(run, isMassive));
				}).catch((error) => {
					if (error instanceof ImportRunPollTimeout) {
						notificate(
							`<strong>La subida sigue procesándose.</strong><br>
							Vuelve a abrir este mes en unos minutos para ver el resultado.`,
							"is-warning is-light"
						);
					} else if (error.response && error.response.status === 409) {
						notificate(
							`<strong>Hay una subida en curso.</strong><br>
							Espera a que termine e inténtalo de nuevo.`,
							"is-warning is-light"
						);
					} else if (error.response && error.response.data && error.response.data.is_dry_run && error.response.data.has_error) {
						this.openDryRunModal([], error.response.data.msg_error);
					} else {
						notificate(
							`<strong>Error!</strong><br>
							Ocurrió un error al cargar el documento`,
							"is-danger is-light"
						);
					}
				}).finally(() => {
					this.isUploadingFile = false;
					if (this.$refs.fileInput) this.$refs.fileInput.value = '';
					if (this.$refs.fileInputMassive) this.$refs.fileInputMassive.value = '';
				})
			} else {
				this.isUploadingFile = false;
			}
		},
		applyUploadRun(run, isMassive) {
			// `result` es la planilla del mes como la devolvía el POST sincrónico
			// (con has_error/msg_error si el archivo se rechazó); viene vacío solo
			// si la task murió antes de registrar la planilla.
			const spreadsheet = run.result && run.result.id ? run.result : null;
			if (isMassive) {
				this.changeYear();
			} else if (spreadsheet) {
				this.allMonthsData = { ...this.allMonthsData, [`${this.selectedDate.year}_${this.selectedDate.month}`]: { ...spreadsheet } };
				this.selectedMonthFile = { ...spreadsheet };
			}
			if (run.status === "S") {
				notificate(
					`<strong>Éxito!</strong><br>
					Documento cargado con éxito`,
					"is-success is-light"
				);
				return;
			}
			// El detalle del rechazo queda además en el panel del mes (msg_error).
			notificate(
				`<strong>No se pudo cargar el documento.</strong><br>
				${run.error || "Revisa el archivo y vuelve a subirlo."}`,
				"is-danger is-light"
			);
		},
		downloadTemplateByTypeFile() {
			this.downloadFile(this.selectedTypeFile.TEMPLATE, this.selectedTypeFile.nameTemplate, { template: true })
		},
		async setLastMonth() {
			let currentIndex = this.listMonthsNames.indexOf(this.selectedDate.month);
			let usedYear = this.selectedDate.year
			if (currentIndex <= 0) {
				currentIndex = this.listMonthsNames.length
				usedYear -= 1
			}
			const previousMonthName = this.listMonthsNames[currentIndex - 1];
			const prevMonthKey = `${usedYear}_${previousMonthName}`;

			if (this.allMonthsData[prevMonthKey]) {
				this.previousMonth = this.allMonthsData[prevMonthKey];
				return;
			}

			try {
				const data = await this.fetchSpreadsheetByMonth({
					'year': usedYear,
					'month': previousMonthName,
					'type': this.selectedTypeFile.value
				});
				this.previousMonth = data.spreadsheet_data || {};
				this.allMonthsData = { ...this.allMonthsData, [prevMonthKey]: this.previousMonth };
			} catch (error) {
				this.previousMonth = {};
			}
		},
		downloadFile(drive_id, file_name, fallbackCtx = null) {
			if (!drive_id) {
				// Mes subido sin url_drive: antes fallaba en silencio.
				this.downloadMonthFallback(file_name, fallbackCtx);
				return;
			}
			getUrlsDownloadFile({
				drive_id,
				file_name,
			}).then(({ data }) => {
				if (data && data.url_download) {
					let url = data.url_download
					url = url.match(/^https?:/) ? url : '//' + url;
					window.open(url)
				} else {
					this.downloadMonthFallback(file_name, fallbackCtx);
				}
			}).catch(() => {
				this.downloadMonthFallback(file_name, fallbackCtx);
			})
		},
		// Fallback cuando el archivo de Drive no está disponible: el backend
		// regenera el xlsx del mes desde la BD (o la plantilla vacía) y se
		// descarga como blob.
		async downloadMonthFallback(file_name, ctx) {
			if (!ctx) {
				notificate(
					`<strong>Error!</strong><br>
					No se pudo descargar el documento`,
					"is-danger is-light"
				);
				return;
			}
			notificate(
				`<strong>Aviso</strong><br>
				El archivo original no está disponible. Descargando desde los datos del mes...`,
				"is-info is-light"
			);
			const params = ctx.template
				? { type: this.selectedTypeFile.value, template: 1 }
				: { type: this.selectedTypeFile.value, year: ctx.year, month: ctx.month };
			try {
				const { data } = await downloadSpreadsheetMonthFile(params);
				downloadBlob(data, file_name || "documentos.xlsx");
				notificate(
					`<strong>Éxito!</strong><br>
					Descarga iniciada.`,
					"is-success is-light"
				);
			} catch (error) {
				console.error("Error downloading month file:", error);
				notificate(
					`<strong>Error!</strong><br>
					No se pudo descargar el documento`,
					"is-danger is-light"
				);
			}
		},
		changeYear() {
			this.fetchFilesUploadedByYear({
				'year': this.selectedDate.year,
				'type': this.selectedTypeFile.value
			}).then((data) => {
				const allSpreadsheetByYear = data.spreadsheet_data || [];
				const allMonths = this.listMonthsNames
				const allDataMonths = {}
				allMonths.forEach(month => {
					allDataMonths[`${this.selectedDate.year}_${month}`] = {}
				})
				allSpreadsheetByYear.forEach(data => {
					allDataMonths[`${data.year_file}_${data.month_file}`] = data
				})

				this.allMonthsData = { ...this.allMonthsData, ...allDataMonths }
				this.changeMonth()
				if (!this.hasCheckedInitialMassive) {
					this.hasCheckedInitialMassive = true;
					this.checkMassiveUploadModal()
				}
			})
		},
		changeMonth() {
			this.setLastMonth()
			this.fetchSpreadsheetByMonth({
				'year': this.selectedDate.year,
				'month': this.selectedDate.month,
				'type': this.selectedTypeFile.value
			}).then((data) => {
				this.selectedMonthFile = data.spreadsheet_data || {}
			})
		},
		deleteSelectedFile() {
			this.deleteSpreadsheetMonth(this.selectedMonthFile.id).then(() => {
				this.allMonthsData = { ...this.allMonthsData, [`${this.selectedDate.year}_${this.selectedDate.month}`]: {} };
				this.selectedMonthFile = {}
				notificate(
					`<strong>Éxito!</strong><br>
					Documento eliminado con éxito`,
					"is-success is-light"
				);
			}).catch(() => {
				notificate(
					`<strong>Error!</strong><br>
					Ocurrió un error al eliminar el documento`,
					"is-danger is-light"
				);
			})
		},
		openConfirmRestoreModal() {
			this.$buefy.modal.open({
				...this.modalDefault,
				component: ConfirmRestore,
				props: {
					eventNameConfirmRestore: 'deleteFile',
					tittleRestore: '¿Estás seguro de eliminar este documento?',
					subTittleRestore: 'Esta acción eliminará el documento de libro de remuneraciones de ',
					endSubTittleBold: this.selectedDate.month + ' ' + this.selectedDate.year,
					msgOkAction: 'Eliminar Archivo'
				},
				events: {
					deleteFile: () => {
						this.deleteSelectedFile()
					}
				},
			});
		},
		async checkMassiveUploadModal() {
			if (this.selectedDate.year !== moment().year()) return;

			let missingCount = 0;
			const currentYear = moment().year();
			const neededYears = [currentYear - 1, currentYear - 2];
			const fetchPromises = [];

			neededYears.forEach(year => {
				if (this.allMonthsData[`${year}_${this.listMonthsNames[0]}`] === undefined) {
					fetchPromises.push(this.fetchFilesUploadedByYear({
						'year': year,
						'type': this.selectedTypeFile.value
					}).then((data) => ({ year, data: data.spreadsheet_data || [] })));
				}
			});

			if (fetchPromises.length > 0) {
				try {
					const results = await Promise.all(fetchPromises);
					let allDataMonths = {};
					results.forEach(({ year, data }) => {
						this.listMonthsNames.forEach(month => {
							allDataMonths[`${year}_${month}`] = {};
						});
						data.forEach(item => {
							allDataMonths[`${item.year_file}_${item.month_file}`] = item;
						});
					});
					this.allMonthsData = { ...this.allMonthsData, ...allDataMonths };
				} catch (error) {
					console.error("Error fetching previous years:", error);
				}
			}

			for (let i = 0; i < 24; i++) {
				const targetMoment = moment().subtract(i, 'months');
				const key = targetMoment.year() + '_' + this.listMonthsNames[targetMoment.month()];
				const fileData = this.allMonthsData[key];

				if (fileData && fileData.id && !fileData.has_error) {
					break;
				}
				missingCount++;
			}

			if (missingCount >= 3) {
				this.openMassiveUploadModal(missingCount);
			}
		},
		confirmDeleteEntry(entry) {
			this.$buefy.modal.open({
				...this.modalDefault,
				component: ConfirmRestore,
				props: {
					eventNameConfirmRestore: 'deleteDocumentEntry',
					tittleRestore: 'Eliminar documento',
					subTittleRestore: '¿Estás seguro de que deseas eliminar este documento? Esta acción no se puede deshacer.',
					endSubTittleBold: '',
					msgOkAction: 'Eliminar',
					cancelText: 'Cancelar',
					btnClass: 'is-danger',
					headerIconBgClass: 'has-background-danger-light',
					iconComponent: Trash
				},
				events: {
					deleteDocumentEntry: async () => {
						try {
							this.isUploadingFile = true;
							await this.removeTaxDocument({ document_id: entry.id });
							notificate(
								`<strong>Éxito!</strong><br>
								Documento eliminado exitosamente`,
								"is-success is-light"
							);
							this.changeYear();
						} catch (error) {
							console.error(error);
							notificate(
								`<strong>Error!</strong><br>
								Ocurrió un error al eliminar el documento`,
								"is-danger is-light"
							);
						} finally {
							this.isUploadingFile = false;
						}
					}
				},
			});
		},
		openMassiveUploadModal(missingCount) {
			let subTittleMessage = `Al parecer no has subido ${this.selectedTypeFile.label} en los últimos ${missingCount} meses. Puedes subirlos de forma masiva en un solo archivo.`;
			if (missingCount >= 24) {
				subTittleMessage = `Al parecer estás por iniciar la subida de ${this.selectedTypeFile.label}. Te invitamos a subirlos de forma masiva en un solo archivo.`;
			}

			this.$buefy.modal.open({
				...this.modalDefault,
				component: ConfirmRestore,
				props: {
					eventNameConfirmRestore: 'openMassiveUpload',
					tittleRestore: '¿Subir múltiples meses?',
					subTittleRestore: subTittleMessage,
					endSubTittleBold: '',
					msgOkAction: 'Subir archivo masivo',
					cancelText: 'Subir un mes',
					btnClass: 'is-primary',
					headerIconBgClass: 'has-background-info-light',
					iconComponent: UploadFile
				},
				events: {
					openMassiveUpload: () => {
						this.$refs.fileInputMassive.click();
					}
				},
			});
		},
		openDryRunModal(summary, msgError = null, warnings = []) {
			let tittleRestore = '¿Confirmar Sobreescritura Masiva?';
			let subTittleRestore = '';
			let endSubTittleBold = ' ¿Proceder?';
			let msgOkAction = 'Confirmar y Subir';
			let btnClass = 'is-info';
			let headerIconBgClass = 'has-background-warning-light';

			if (msgError) {
				tittleRestore = 'Error en el Archivo';
				subTittleRestore = msgError;
				endSubTittleBold = ' Por favor, modifica el archivo e inténtalo de nuevo.';
				msgOkAction = 'Volver a intentar';
				btnClass = 'is-danger';
				headerIconBgClass = 'has-background-danger-light';
			} else {
				// `replaces_ledger`: el mes cae dentro del período del mayor y sus
				// cuentas de remuneraciones (las marcadas por el analista) pasan a
				// «Ajuste Remuneraciones» (decisiones §40).
				const ledgerMark = (item) => item.replaces_ledger ? ' · reemplaza las cuentas de remuneraciones del mayor' : '';
				let monthOverwrites = `<ul class="ml-5 mt-2 mb-2" style="list-style-type: disc;">` + (summary || []).map(item => `<li><span class="is-capitalized has-text-weight-semibold">${item.month} ${item.year}:</span> ${item.old_docs_count} documento(s) actual(es) &#8594; ${item.new_docs_count} nuevo(s)${ledgerMark(item)}</li>`).join('') + `</ul>`;
				if (!summary || summary.length === 0) {
					monthOverwrites = "<br><br><strong>No se detectaron meses procesables en tu documento.</strong>";
				}
				const warningBlocks = (warnings || []).map(w => `<p class="mt-2"><strong>${w.title}.</strong> ${w.detail}</p>`).join('');
				subTittleRestore = 'Si continúas, los siguientes meses serán limpiados por completo y sobreescritos por el archivo: ' + monthOverwrites + warningBlocks;
			}

			this.$buefy.modal.open({
				...this.modalDefault,
				component: ConfirmRestore,
				props: {
					eventNameConfirmRestore: msgError ? 'retryMassiveUpload' : 'confirmMassiveUpload',
					tittleRestore: tittleRestore,
					subTittleRestore: subTittleRestore,
					endSubTittleBold: endSubTittleBold,
					msgOkAction: msgOkAction,
					cancelText: 'Cancelar',
					btnClass: btnClass,
					headerIconBgClass: headerIconBgClass,
					iconComponent: msgError ? Page : UploadFile
				},
				events: {
					confirmMassiveUpload: () => {
						this.handleFile(null, true, false);
					},
					retryMassiveUpload: () => {
						this.$refs.fileInputMassive.click();
					}
				},
			});
		},
	}
}
</script>
<style scoped lang="scss">
.border_left {
	border-left: 1px solid #EAECF0;
}
</style>
